import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Upload, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';

const BORDER_RADIUS_OPTIONS = [
  { value: '14px', label: 'Rund (Standard)' },
  { value: '8px', label: 'Leicht gerundet' },
  { value: '2px', label: 'Eckig' },
  { value: '24px', label: 'Pill' },
];

const DEFAULT_PRIMARY = '#1B2A4A';

function WidgetPreview({ config, isDark }) {
  const primary = config.primary_color || DEFAULT_PRIMARY;
  const radius = config.border_radius || '14px';
  const logoUrl = config.logo_url || null;
  const is3d = config.fab_style === '3d';

  const fabStyle = is3d
    ? {
        background: `linear-gradient(180deg, #263861 0%, ${primary} 40%, #111e33 100%)`,
        borderRadius: '100px',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 1px 0 0 rgba(255,255,255,0.08) inset, 0 -2px 0 0 rgba(0,0,0,0.15) inset, 0 8px 24px rgba(0,0,0,0.16), 0 20px 40px -12px rgba(27,42,74,0.25)',
      }
    : { background: primary, borderRadius: '100px' };

  return (
    <div className={`rounded-lg border p-4 ${isDark ? "bg-slate-950 border-slate-700" : "bg-slate-100 border-slate-300"}`}>
      <p className={`text-xs font-medium mb-3 ${isDark ? "text-slate-400" : "text-slate-500"}`}>Vorschau</p>
      <div className="flex flex-col items-end gap-3">
        {/* FAB preview */}
        <div
          className="flex items-center gap-2 text-white text-sm font-medium px-4 py-2 shadow-lg"
          style={fabStyle}
        >
          {logoUrl && <img src={logoUrl} alt="" className="w-6 h-6 object-contain" onError={e => { e.target.style.display = 'none'; }} />}
          <span>{config.fab_label || 'Frag StadtKI'}</span>
        </div>

        {/* Window preview */}
        <div
          className="w-full shadow-lg overflow-hidden flex flex-col"
          style={{ borderRadius: radius, border: '1px solid #E5E5E0', maxWidth: 280 }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-2 text-white text-xs" style={{ background: primary }}>
            {logoUrl && <img src={logoUrl} alt="" className="w-5 h-5 object-contain" onError={e => { e.target.style.display = 'none'; }} />}
            <div>
              <div className="font-semibold" style={{ fontSize: 11 }}>{config.header_title || 'Stadtverwaltung'}</div>
              <div style={{ fontSize: 9, opacity: 0.6 }}>{config.header_subtitle || 'StadtKI'}</div>
            </div>
          </div>
          {/* Body */}
          <div className="bg-white px-3 py-2 space-y-1.5">
            <div className="text-xs px-2 py-1.5 rounded-lg bg-gray-100 text-gray-700" style={{ borderRadius: `${radius} ${radius} ${radius} 3px`, maxWidth: '85%' }}>
              {config.greeting_text || 'Hallo, stellen Sie Fragen zu unserem Service.'}
            </div>
            <div className="text-xs px-2 py-1.5 rounded-lg text-white self-end ml-auto" style={{ background: primary, borderRadius: `${radius} ${radius} 3px ${radius}`, maxWidth: '75%' }}>
              Wann hat das Rathaus offen?
            </div>
          </div>
          {/* Footer */}
          <div className="bg-white border-t px-3 py-1.5 flex gap-1.5 items-center" style={{ borderColor: '#E5E5E0' }}>
            <div className="flex-1 text-xs border rounded-md px-2 py-1 text-gray-400" style={{ borderColor: '#D1D1CC' }}>
              Nachricht eintippen...
            </div>
            <div className="w-5 h-5 rounded-md flex items-center justify-center text-white" style={{ background: primary, fontSize: 8 }}>
              &#10148;
            </div>
          </div>
          {/* Privacy */}
          <div className="text-center py-1 border-t" style={{ fontSize: 7, color: '#9B9BA3', borderColor: '#E5E5E0' }}>
            DSGVO-konform · Hosted in Deutschland
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WidgetConfigTab({ isDark, userInfo, selectedMunicipalityId }) {
  const [widgetUIConfig, setWidgetUIConfig] = useState({
    greeting_text: '',
    privacy_url: '',
    privacy_label: 'Datenschutz',
    logo_url: '',
    primary_color: '',
    border_radius: '',
    header_title: '',
    header_subtitle: '',
    fab_label: '',
    fab_style: '',
  });
  const [loadingWidgetConfig, setLoadingWidgetConfig] = useState(false);
  const [savingWidgetConfig, setSavingWidgetConfig] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  const getMuniParam = () => {
    if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) return selectedMunicipalityId;
    return null;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) { toast.error('Logo zu gross (max 500KB)'); return; }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const muniId = getMuniParam();
      const url = muniId ? `/municipality/logo?municipality_id=${muniId}` : '/municipality/logo';
      const response = await apiClient.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setWidgetUIConfig(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo hochgeladen');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Upload fehlgeschlagen';
      toast.error(`Logo-Upload fehlgeschlagen: ${msg}`);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    try {
      const muniId = getMuniParam();
      const url = muniId ? `/municipality/logo?municipality_id=${muniId}` : '/municipality/logo';
      await apiClient.delete(url);
      setWidgetUIConfig(prev => ({ ...prev, logo_url: '' }));
      toast.success('Logo entfernt');
    } catch (error) {
      toast.error('Fehler beim Entfernen des Logos');
    }
  };

  const fetchWidgetUIConfig = async () => {
    try {
      setLoadingWidgetConfig(true);
      const params = userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId
        ? { municipality_id: selectedMunicipalityId }
        : {};
      const response = await apiClient.get('/municipality/ui-config', { params });
      setWidgetUIConfig({
        greeting_text: response.data.greeting_text || '',
        privacy_url: response.data.privacy_url || '',
        privacy_label: response.data.privacy_label || 'Datenschutz',
        logo_url: response.data.logo_url || '',
        primary_color: response.data.primary_color || '',
        border_radius: response.data.border_radius || '',
        header_title: response.data.header_title || '',
        header_subtitle: response.data.header_subtitle || '',
        fab_label: response.data.fab_label || '',
        fab_style: response.data.fab_style || '',
      });
    } catch (error) {
      console.error('Error fetching widget UI config:', error);
      if (error.response?.status !== 404) {
        toast.error('Fehler beim Laden der Widget-Einstellungen');
      }
    } finally {
      setLoadingWidgetConfig(false);
    }
  };

  const handleSaveWidgetConfig = async (e) => {
    e.preventDefault();
    try {
      setSavingWidgetConfig(true);
      const payload = {
        greeting_text: widgetUIConfig.greeting_text || null,
        privacy_url: widgetUIConfig.privacy_url || null,
        privacy_label: widgetUIConfig.privacy_label || 'Datenschutz',
        logo_url: widgetUIConfig.logo_url || null,
        primary_color: widgetUIConfig.primary_color || null,
        border_radius: widgetUIConfig.border_radius || null,
        header_title: widgetUIConfig.header_title || null,
        header_subtitle: widgetUIConfig.header_subtitle || null,
        fab_label: widgetUIConfig.fab_label || null,
        fab_style: widgetUIConfig.fab_style || null,
      };
      if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) {
        payload.municipality_id = selectedMunicipalityId;
      }
      await apiClient.patch('/municipality/ui-config', payload);
      toast.success('Widget-Einstellungen erfolgreich gespeichert');
    } catch (error) {
      console.error('Error saving widget UI config:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Fehler beim Speichern';
      toast.error(`Fehler beim Speichern: ${errorMessage}`);
    } finally {
      setSavingWidgetConfig(false);
    }
  };

  useEffect(() => {
    if (userInfo?.role === 'MunicipalityAdmin' || (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId)) {
      fetchWidgetUIConfig();
    }
  }, [userInfo?.role, selectedMunicipalityId]);

  const inputClass = isDark
    ? "bg-slate-950 border-slate-700 text-white"
    : "bg-white border-slate-300 text-slate-900";

  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Widget-Einstellungen</h2>
      </div>

      <Card className={isDark 
        ? "bg-slate-900 border-slate-800 shadow-lg" 
        : "bg-slate-50 border-slate-200 shadow-lg"
      }>
        <CardHeader>
          <CardTitle className={isDark ? "text-white" : "text-slate-900"}>Widget-UI-Konfiguration</CardTitle>
          <CardDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
            Konfigurieren Sie Aussehen, Begrüßung und Datenschutz-Link für das Widget.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {userInfo?.role === 'ProviderAdmin' && !selectedMunicipalityId ? (
            <div className={`h-32 flex items-center justify-center ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              <span className="text-sm">Bitte wählen Sie eine Gemeinde aus dem Filter oben, um die Widget-Einstellungen anzuzeigen.</span>
            </div>
          ) : loadingWidgetConfig ? (
            <div className={`h-32 flex items-center justify-center ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Lade Einstellungen...
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Form */}
              <form onSubmit={handleSaveWidgetConfig} className="space-y-4">
                {/* Design section */}
                <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800/50 border border-slate-700" : "bg-white border border-slate-200"}`}>
                  <p className={`text-sm font-medium mb-3 ${isDark ? "text-slate-200" : "text-slate-700"}`}>Design</p>

                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Logo</Label>
                      {widgetUIConfig.logo_url && (
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded border flex items-center justify-center overflow-hidden ${isDark ? "border-slate-600 bg-slate-800" : "border-slate-300 bg-white"}`}>
                            <img src={widgetUIConfig.logo_url} alt="Logo" className="w-10 h-10 object-contain" onError={e => { e.target.style.display = 'none'; }} />
                          </div>
                          <Button type="button" variant="outline" size="sm" onClick={handleLogoDelete}
                            className={`text-red-500 hover:text-red-400 ${isDark ? "border-slate-700 hover:bg-slate-800" : "border-slate-300 hover:bg-slate-100"}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Entfernen
                          </Button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input type="file" ref={logoInputRef} accept="image/png,image/jpeg,image/svg+xml" onChange={handleLogoUpload} className="hidden" />
                        <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                          className={isDark ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"}>
                          {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                          {uploadingLogo ? 'Hochladen...' : 'Logo hochladen'}
                        </Button>
                      </div>
                      <details className="text-xs">
                        <summary className={`cursor-pointer ${isDark ? "text-slate-400" : "text-slate-500"}`}>oder URL eingeben</summary>
                        <Input
                          type="text"
                          value={widgetUIConfig.logo_url}
                          onChange={e => setWidgetUIConfig({...widgetUIConfig, logo_url: e.target.value})}
                          placeholder="https://example.com/logo.png"
                          maxLength={500}
                          className={`mt-1 ${inputClass}`}
                        />
                      </details>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        PNG oder JPEG, max 500KB, transparent empfohlen
                      </p>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="primary_color">Primärfarbe</Label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={widgetUIConfig.primary_color || DEFAULT_PRIMARY}
                          onChange={e => setWidgetUIConfig({...widgetUIConfig, primary_color: e.target.value})}
                          className="w-10 h-10 rounded border cursor-pointer p-0.5"
                          style={{ borderColor: isDark ? '#334155' : '#cbd5e1' }}
                        />
                        <Input
                          id="primary_color"
                          value={widgetUIConfig.primary_color}
                          onChange={e => setWidgetUIConfig({...widgetUIConfig, primary_color: e.target.value})}
                          placeholder={DEFAULT_PRIMARY}
                          maxLength={20}
                          className={`font-mono flex-1 ${inputClass}`}
                        />
                      </div>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        Wird für Header, FAB und Nutzer-Nachrichten verwendet
                      </p>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="border_radius">Eckenform</Label>
                      <Select
                        value={widgetUIConfig.border_radius || '14px'}
                        onValueChange={val => setWidgetUIConfig({...widgetUIConfig, border_radius: val})}
                      >
                        <SelectTrigger className={inputClass}>
                          <SelectValue placeholder="Eckenform wählen" />
                        </SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}>
                          {BORDER_RADIUS_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="fab_style">Button-Stil</Label>
                      <Select
                        value={widgetUIConfig.fab_style || 'default'}
                        onValueChange={val => setWidgetUIConfig({...widgetUIConfig, fab_style: val})}
                      >
                        <SelectTrigger className={inputClass}>
                          <SelectValue placeholder="Button-Stil wählen" />
                        </SelectTrigger>
                        <SelectContent className={isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}>
                          <SelectItem value="default">Flat (Standard)</SelectItem>
                          <SelectItem value="3d">3D-Effekt</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        3D-Effekt zeigt Gradient, Tiefe und Hover-Animation
                      </p>
                    </div>
                  </div>
                </div>

                {/* Content section */}
                <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800/50 border border-slate-700" : "bg-white border border-slate-200"}`}>
                  <p className={`text-sm font-medium mb-3 ${isDark ? "text-slate-200" : "text-slate-700"}`}>Inhalt</p>

                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="header_title">Header-Titel</Label>
                        <Input
                          id="header_title"
                          value={widgetUIConfig.header_title}
                          onChange={e => setWidgetUIConfig({...widgetUIConfig, header_title: e.target.value})}
                          placeholder="Stadtverwaltung"
                          maxLength={100}
                          className={inputClass}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="header_subtitle">Header-Untertitel</Label>
                        <Input
                          id="header_subtitle"
                          value={widgetUIConfig.header_subtitle}
                          onChange={e => setWidgetUIConfig({...widgetUIConfig, header_subtitle: e.target.value})}
                          placeholder="StadtKI"
                          maxLength={100}
                          className={inputClass}
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="fab_label">Button-Text (geschlossen)</Label>
                      <Input
                        id="fab_label"
                        value={widgetUIConfig.fab_label}
                        onChange={e => setWidgetUIConfig({...widgetUIConfig, fab_label: e.target.value})}
                        placeholder="Frag StadtKI"
                        maxLength={60}
                        className={inputClass}
                      />
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        Text neben dem Logo im geschlossenen Widget-Button
                      </p>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="greeting_text">Begrüßungstext</Label>
                      <textarea
                        id="greeting_text"
                        value={widgetUIConfig.greeting_text}
                        onChange={e => setWidgetUIConfig({...widgetUIConfig, greeting_text: e.target.value})}
                        placeholder="Hallo, ich bin die StadtKI..."
                        maxLength={500}
                        rows={3}
                        className={`w-full p-2 rounded border text-sm ${
                          isDark 
                            ? "bg-slate-950 border-slate-700 text-white placeholder-slate-500" 
                            : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
                        } focus:outline-none focus:ring-2 focus:ring-sky-500`}
                      />
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {widgetUIConfig.greeting_text.length}/500 Zeichen
                      </p>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="privacy_url">Datenschutz-URL</Label>
                      <Input
                        id="privacy_url"
                        type="url"
                        value={widgetUIConfig.privacy_url}
                        onChange={e => setWidgetUIConfig({...widgetUIConfig, privacy_url: e.target.value})}
                        placeholder="https://example.com/datenschutz"
                        maxLength={300}
                        className={inputClass}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="privacy_label">Datenschutz-Label</Label>
                      <Input
                        id="privacy_label"
                        value={widgetUIConfig.privacy_label}
                        onChange={e => setWidgetUIConfig({...widgetUIConfig, privacy_label: e.target.value})}
                        placeholder="Datenschutz"
                        maxLength={40}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <Button 
                    type="submit" 
                    disabled={savingWidgetConfig} 
                    className="bg-sky-600 hover:bg-sky-500 text-white"
                  >
                    {savingWidgetConfig ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Speichere...
                      </>
                    ) : (
                      'Speichern'
                    )}
                  </Button>
                </div>
              </form>

              {/* Right: Live preview */}
              <div className="lg:sticky lg:top-4">
                <WidgetPreview config={widgetUIConfig} isDark={isDark} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
